<template>
    <Card pageTitle="Customer Account Automation">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_CUSTOMER">
            <div class="settings-wrap">
                <p class="description">
                    Configure automatic customer account discounts and signup email messaging.
                </p>

                <div class="field-row checkbox-row">
                    <input
                        id="first-order-discount-enabled"
                        type="checkbox"
                        v-model="firstOrderDiscountEnabled"
                        :disabled="!canEdit || saving"
                    />
                    <label for="first-order-discount-enabled">Enable first-order discount for new customers</label>
                </div>

                <div class="field-row">
                    <div class="field-label">First-order discount (%)</div>
                    <CustomInput
                        type="number"
                        :step="0.01"
                        v-model="firstOrderDiscountPercent"
                        :disabled="!canEdit || saving || !firstOrderDiscountEnabled"
                    />
                </div>

                <div class="field-row checkbox-row">
                    <input
                        id="signup-email-enabled"
                        type="checkbox"
                        v-model="signupEmailEnabled"
                        :disabled="!canEdit || saving"
                    />
                    <label for="signup-email-enabled">Send signup email when a customer account is created</label>
                </div>

                <div class="template-callout">
                    <div>
                        <strong>Welcome email design</strong>
                        <span>
                            Build and test the branded customer email separately. The subject and message below remain the safe fallback until an administrator publishes a custom template.
                        </span>
                    </div>
                    <router-link to="/settings/email-templates/customer_welcome">
                        Open email builder
                        <span class="material-icons">arrow_forward</span>
                    </router-link>
                </div>

                <div class="field-row">
                    <div class="field-label">Signup email subject</div>
                    <CustomInput
                        v-model="signupEmailSubject"
                        :disabled="!canEdit || saving || !signupEmailEnabled"
                    />
                </div>

                <div class="field-row">
                    <div class="field-label">Signup email message</div>
                    <textarea
                        class="message-input"
                        rows="6"
                        v-model="signupEmailMessage"
                        :disabled="!canEdit || saving || !signupEmailEnabled"
                    ></textarea>
                    <div class="hint" v-pre>
                        Available placeholders: {{first_name}}, {{last_name}}, {{customer_name}}, {{email}}, {{phone}},
                        {{first_order_discount_percent}}, {{first_order_offer}}
                    </div>
                </div>

                <div class="field-row checkbox-row">
                    <input
                        id="signup-sms-enabled"
                        type="checkbox"
                        v-model="signupSmsEnabled"
                        :disabled="!canEdit || saving"
                    />
                    <label for="signup-sms-enabled">Send signup text message when a customer account is created</label>
                </div>

                <div class="field-row">
                    <div class="field-label">Signup text message</div>
                    <textarea
                        class="message-input"
                        rows="4"
                        v-model="signupSmsMessage"
                        :disabled="!canEdit || saving || !signupSmsEnabled"
                    ></textarea>
                </div>

                <div class="policy-preview">
                    Current first-order discount:
                    <strong>{{ firstOrderDiscountEnabled ? `${normalizedDiscount}%` : "Disabled" }}</strong>
                    <br />
                    Signup email:
                    <strong>{{ signupEmailEnabled ? "Enabled" : "Disabled" }}</strong>
                    <br />
                    Signup text:
                    <strong>{{ signupSmsEnabled ? "Enabled" : "Disabled" }}</strong>
                </div>

                <div class="actions">
                    <Button borderOnly class="me-2" @click="loadConfig" :disabled="loading || saving">
                        Reload
                    </Button>
                    <Button v-if="canEdit" @click="saveConfig" :disabled="loading || saving">
                        {{ saving ? "Saving..." : "Save" }}
                    </Button>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import CashManagementFetchApi from "../../utils/fetch/cash-management-fetch-api"
import { errMsg, round2 } from "../../utils/common"

export default {
    components: {
        Card,
        Button,
        CustomInput,
        AllowPermission,
    },
    data() {
        return {
            PERMISSION_CODES,
            loading: false,
            saving: false,
            firstOrderDiscountEnabled: true,
            firstOrderDiscountPercent: "15",
            signupEmailEnabled: false,
            signupEmailSubject: "Welcome to High Ties",
            signupEmailMessage:
                "Hi {{first_name}}, welcome to High Ties. Your customer account has been created. {{first_order_offer}}",
            signupSmsEnabled: false,
            signupSmsMessage: "Hi {{first_name}}, welcome to High Ties. Your customer account has been created.",
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_CUSTOMER,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        normalizedDiscount() {
            return round2(Number(this.firstOrderDiscountPercent || 0))
        },
    },
    async mounted() {
        await this.loadConfig()
    },
    methods: {
        async loadConfig() {
            this.loading = true
            try {
                const config = await CashManagementFetchApi.fetchAccountAutomationSettings()
                this.firstOrderDiscountEnabled = !!config?.firstOrderDiscountEnabled
                this.firstOrderDiscountPercent = String(config?.firstOrderDiscountPercent ?? 15)
                this.signupEmailEnabled = !!config?.signupEmailEnabled
                this.signupEmailSubject = config?.signupEmailSubject || "Welcome to High Ties"
                this.signupEmailMessage =
                    config?.signupEmailMessage ||
                    "Hi {{first_name}}, welcome to High Ties. Your customer account has been created. {{first_order_offer}}"
                this.signupSmsEnabled = !!config?.signupSmsEnabled
                this.signupSmsMessage = config?.signupSmsMessage || "Hi {{first_name}}, welcome to High Ties. Your customer account has been created."
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load account automation settings"))
            }
            this.loading = false
        },
        async saveConfig() {
            if (!this.canEdit || this.saving) return
            const discount = Number(this.firstOrderDiscountPercent)
            if (!Number.isFinite(discount) || discount < 0 || discount > 100) {
                return this.$toast.error("First-order discount must be between 0 and 100")
            }
            if (!this.signupEmailSubject.trim()) {
                return this.$toast.error("Signup email subject is required")
            }
            if (!this.signupEmailMessage.trim()) {
                return this.$toast.error("Signup email message is required")
            }
            if (!this.signupSmsMessage.trim()) {
                return this.$toast.error("Signup text message is required")
            }

            this.saving = true
            try {
                const updated = await CashManagementFetchApi.updateAccountAutomationSettings({
                    firstOrderDiscountEnabled: !!this.firstOrderDiscountEnabled,
                    firstOrderDiscountPercent: discount,
                    signupEmailEnabled: !!this.signupEmailEnabled,
                    signupEmailSubject: this.signupEmailSubject,
                    signupEmailMessage: this.signupEmailMessage,
                    signupSmsEnabled: !!this.signupSmsEnabled,
                    signupSmsMessage: this.signupSmsMessage,
                })
                this.firstOrderDiscountEnabled = !!updated?.firstOrderDiscountEnabled
                this.firstOrderDiscountPercent = String(updated?.firstOrderDiscountPercent ?? discount)
                this.signupEmailEnabled = !!updated?.signupEmailEnabled
                this.signupEmailSubject = updated?.signupEmailSubject || this.signupEmailSubject
                this.signupEmailMessage = updated?.signupEmailMessage || this.signupEmailMessage
                this.signupSmsEnabled = !!updated?.signupSmsEnabled
                this.signupSmsMessage = updated?.signupSmsMessage || this.signupSmsMessage
                this.$toast.success("Account automation settings updated")
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not update account automation settings"))
            }
            this.saving = false
        },
    },
}
</script>

<style lang="scss" scoped>
.settings-wrap {
    max-width: 760px;
}

.template-callout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 18px;
    margin: 18px 0;
    padding: 16px 18px;
    border-left: 4px solid #b8864c;
    background: #f7f3ec;
    color: #53655f;
}

.template-callout strong,
.template-callout span {
    display: block;
}

.template-callout strong {
    margin-bottom: 3px;
    color: #173b35;
}

.template-callout span {
    font-size: 13px;
    line-height: 1.45;
}

.template-callout a {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #173b35;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
}

.template-callout a .material-icons {
    font-size: 17px;
}

@media (max-width: 720px) {
    .template-callout {
        grid-template-columns: 1fr;
    }
}

.description {
    margin-bottom: 16px;
    color: #5f6368;
}

.field-row {
    margin-bottom: 16px;
}

.field-label {
    font-weight: 600;
    margin-bottom: 6px;
}

.checkbox-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.message-input {
    width: 100%;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    padding: 10px 12px;
    resize: vertical;
}

.hint {
    color: #6b7280;
    font-size: 13px;
    margin-top: 6px;
}

.policy-preview {
    padding: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 16px;
}

.actions {
    display: flex;
    align-items: center;
}
</style>
